import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileText, Download, Mail, MessageCircle, Printer,
  User, Hotel, Calendar, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useTabs } from '@/context/TabsContext';

export default function InvoiceViewTab({ tabId, data }) {
  const { closeTab } = useTabs();
  const { invoiceId, invoice: initialInvoice } = data || {};
  
  const [invoice, setInvoice] = useState(initialInvoice || null);
  const [loading, setLoading] = useState(!initialInvoice);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!initialInvoice && invoiceId) {
      loadInvoice();
    }
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      const res = await api.get(`/invoices/${invoiceId}`);
      setInvoice(res.data);
    } catch (error) {
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoice_number || invoice.id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    setSending(true);
    try {
      await api.post(`/invoices/${invoice.id}/send`, {
        delivery_method: 'email'
      });
      toast.success('Invoice sent via email');
    } catch (error) {
      toast.error('Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        <span className="ml-2 text-gray-600">Loading invoice...</span>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Invoice not found</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileText className="h-6 w-6 text-emerald-600" />
            Invoice #{invoice.invoice_number || invoice.id?.slice(-8)}
          </h1>
          <p className="text-gray-500 mt-1">
            Created on {new Date(invoice.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDownload} disabled={downloading}>
            {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Download PDF
          </Button>
          <Button variant="outline" onClick={handleSendEmail} disabled={sending}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
            Send Email
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-3 gap-6 overflow-hidden">
        <ScrollArea className="col-span-2 pr-4">
          <div className="space-y-4 pb-4">
            {/* Header Info */}
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Guest</p>
                    <p className="font-medium text-gray-800 flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {invoice.guest_name || 'Guest'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Room</p>
                    <p className="font-medium text-gray-800 flex items-center gap-2">
                      <Hotel className="h-4 w-4 text-gray-400" />
                      {invoice.room_number || 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card>
              <CardHeader className="py-3 px-4 bg-gray-50 border-b">
                <CardTitle className="text-sm font-medium">Invoice Items</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-2 text-left">Description</th>
                      <th className="px-4 py-2 text-right">Qty</th>
                      <th className="px-4 py-2 text-right">Rate</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {invoice.items?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-4 py-3">{item.description}</td>
                        <td className="px-4 py-3 text-right">{item.quantity || 1}</td>
                        <td className="px-4 py-3 text-right">₹{(item.unit_price || item.amount)?.toFixed(0)}</td>
                        <td className="px-4 py-3 text-right font-medium">₹{item.amount?.toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Tax Breakdown */}
            {invoice.tax_breakdown && (
              <Card>
                <CardHeader className="py-3 px-4 bg-gray-50 border-b">
                  <CardTitle className="text-sm font-medium">Tax Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-2 text-sm">
                  {invoice.tax_breakdown.room_tax?.amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Room Tax ({invoice.tax_breakdown.room_tax.rate}%)</span>
                      <span>₹{invoice.tax_breakdown.room_tax.amount?.toFixed(0)}</span>
                    </div>
                  )}
                  {invoice.tax_breakdown.restaurant_tax?.amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Restaurant Tax ({invoice.tax_breakdown.restaurant_tax.rate}%)</span>
                      <span>₹{invoice.tax_breakdown.restaurant_tax.amount?.toFixed(0)}</span>
                    </div>
                  )}
                  {invoice.tax_breakdown.service_tax?.amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service Tax ({invoice.tax_breakdown.service_tax.rate}%)</span>
                      <span>₹{invoice.tax_breakdown.service_tax.amount?.toFixed(0)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Payment Transactions */}
            {invoice.payment_transactions?.length > 0 && (
              <Card>
                <CardHeader className="py-3 px-4 bg-green-50 border-b">
                  <CardTitle className="text-sm font-medium text-green-700">Payment Details</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-2">
                  {invoice.payment_transactions.map((txn, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded">
                      <div>
                        <span className="font-medium capitalize">{txn.method}</span>
                        {txn.transaction_id && <span className="text-gray-500 ml-2">• Ref: {txn.transaction_id}</span>}
                        {txn.card_last_four && <span className="text-gray-500 ml-2">• **** {txn.card_last_four}</span>}
                        {txn.upi_id && <span className="text-gray-500 ml-2">• {txn.upi_id}</span>}
                      </div>
                      <span className="font-semibold text-green-600">₹{txn.amount?.toFixed(0)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>

        {/* Right Column - Summary */}
        <div>
          <Card>
            <CardHeader className="py-3 px-4 bg-gradient-to-r from-emerald-50 to-blue-50 border-b">
              <CardTitle className="text-sm font-medium">Invoice Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">₹{invoice.subtotal?.toFixed(0) || 0}</span>
              </div>
              {invoice.discount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Discount</span>
                  <span>- ₹{invoice.discount?.toFixed(0)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Tax</span>
                <span className="font-medium">₹{invoice.tax?.toFixed(0) || 0}</span>
              </div>
              <div className="border-t pt-2 flex justify-between text-lg">
                <span className="font-bold">Total</span>
                <span className="font-bold text-emerald-600">₹{invoice.total_amount?.toFixed(0) || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Paid</span>
                <span className="font-medium text-green-600">₹{invoice.paid_amount?.toFixed(0) || 0}</span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span className="font-medium">Balance</span>
                <span className={`font-bold ${invoice.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{invoice.balance_due?.toFixed(0) || 0}
                </span>
              </div>

              <div className="pt-4">
                <Badge className={`w-full justify-center py-2 ${
                  invoice.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                  invoice.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {invoice.payment_status?.toUpperCase() || 'UNPAID'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Button
            variant="outline"
            onClick={() => closeTab(tabId)}
            className="w-full mt-4"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
